import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  UnitMutationWithParties,
  UnitMutationPartyWithOwner,
  CreateUnitMutationInput,
} from '../types/mutations'

/**
 * Hook pour récupérer toutes les mutations d'un lot
 */
export function useUnitMutations(unitId: string | undefined) {
  return useQuery({
    queryKey: ['unit-mutations', unitId],
    queryFn: async () => {
      if (!unitId) return []

      // Récupérer les mutations
      const { data: mutations, error: mutationsError } = await supabase
        .from('unit_mutations')
        .select('*')
        .eq('unit_id', unitId)
        .order('mutation_date', { ascending: false })

      if (mutationsError) throw mutationsError

      // Pour chaque mutation, récupérer les parties avec les infos des propriétaires
      const mutationsWithParties: UnitMutationWithParties[] = await Promise.all(
        mutations.map(async (mutation: any) => {
          // Récupérer les parties
          const { data: parties, error: partiesError } = await supabase
            .from('unit_mutation_parties')
            .select(`
              *,
              owner:owners(
                id,
                first_name,
                last_name,
                email
              )
            `)
            .eq('mutation_id', mutation.id)

          if (partiesError) throw partiesError

          // Récupérer les documents joints
          const { data: documents, error: documentsError } = await supabase
            .from('unit_mutation_documents')
            .select('*')
            .eq('mutation_id', mutation.id)

          if (documentsError) console.error('Error fetching documents:', documentsError)

          // Séparer anciens et nouveaux propriétaires
          const former_parties: UnitMutationPartyWithOwner[] = []
          const new_parties: UnitMutationPartyWithOwner[] = []

          parties.forEach((party: any) => {
            // Gérer le cas où la relation owner n'est pas chargée
            const owner = party.owner || null

            const partyWithOwner: UnitMutationPartyWithOwner = {
              ...party,
              owner,
            }

            if (party.party_type === 'former') {
              former_parties.push(partyWithOwner)
            } else {
              new_parties.push(partyWithOwner)
            }
          })

          return {
            ...mutation,
            former_parties,
            new_parties,
            documents: documents || [],
          }
        })
      )

      return mutationsWithParties
    },
    enabled: !!unitId,
  })
}

/**
 * Hook pour créer une nouvelle mutation de lot
 */
export function useCreateUnitMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateUnitMutationInput) => {
      // Préparer les données pour l'appel RPC
      const formerPartiesJson = input.former_parties.map((party) => ({
        owner_id: party.owner_id,
        share_numerator: party.share_numerator,
        share_denominator: party.share_denominator,
        ownership_type: party.ownership_type || 'full',
      }))

      const newPartiesJson = input.new_parties.map((party) => ({
        owner_id: party.owner_id,
        share_numerator: party.share_numerator,
        share_denominator: party.share_denominator,
        ownership_type: party.ownership_type || 'full',
      }))

      // Appeler la fonction RPC
      const { data: mutationId, error } = await supabase.rpc('create_unit_mutation', {
        p_unit_id: input.unit_id,
        p_mutation_date: input.mutation_date,
        p_mutation_type: input.mutation_type,
        p_former_parties: formerPartiesJson,
        p_new_parties: newPartiesJson,
        p_mutation_subtype: input.mutation_subtype ?? undefined,
        p_is_complete: input.is_complete ?? true,
        p_notary_name: input.notary_name ?? undefined,
        p_deed_reference: input.deed_reference ?? undefined,
        p_deed_date: input.deed_date ?? undefined,
        p_sale_price: input.sale_price ?? undefined,
        p_mutation_fees: input.mutation_fees ?? undefined,
        p_notes: input.notes ?? undefined,
      })

      if (error) throw error

      // Upload documents if any
      if (input.documents && input.documents.length > 0 && mutationId) {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        for (const file of input.documents) {
          // Upload file to storage
          const fileName = `${mutationId}/${Date.now()}-${file.name}`
          const { error: uploadError } = await supabase.storage
            .from('unit-mutation-documents')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false,
            })

          if (uploadError) {
            console.error('Error uploading file:', uploadError)
            continue // Continue with other files even if one fails
          }

          // Insert document record
          const { error: insertError } = await supabase
            .from('unit_mutation_documents')
            .insert({
              mutation_id: mutationId,
              file_name: file.name,
              file_path: fileName,
              file_size: file.size,
              mime_type: file.type,
              uploaded_by: user.id,
            })

          if (insertError) {
            console.error('Error inserting document record:', insertError)
          }
        }
      }

      return mutationId
    },
    onSuccess: (_, variables) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['unit-mutations', variables.unit_id] })
      queryClient.invalidateQueries({ queryKey: ['unit-ownerships', variables.unit_id] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

/**
 * Hook pour supprimer une mutation
 */
export function useDeleteUnitMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ mutationId }: { mutationId: string; unitId: string }) => {
      const { error } = await supabase.from('unit_mutations').delete().eq('id', mutationId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['unit-mutations', variables.unitId] })
      queryClient.invalidateQueries({ queryKey: ['unit-ownerships', variables.unitId] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

/**
 * Hook pour mettre à jour une mutation existante
 */
export function useUpdateUnitMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      mutationId,
      updates,
    }: {
      mutationId: string
      unitId: string
      updates: Partial<CreateUnitMutationInput>
    }) => {
      const { error } = await supabase
        .from('unit_mutations')
        .update({
          mutation_date: updates.mutation_date,
          mutation_type: updates.mutation_type,
          mutation_subtype: updates.mutation_subtype,
          is_complete: updates.is_complete,
          notary_name: updates.notary_name,
          deed_reference: updates.deed_reference,
          deed_date: updates.deed_date,
          sale_price: updates.sale_price,
          mutation_fees: updates.mutation_fees,
          notes: updates.notes,
        })
        .eq('id', mutationId)

      if (error) throw error

      // Si les parties ont changé, les mettre à jour aussi
      if (updates.former_parties || updates.new_parties) {
        // D'abord supprimer les anciennes parties
        const { error: deleteError } = await supabase
          .from('unit_mutation_parties')
          .delete()
          .eq('mutation_id', mutationId)

        if (deleteError) throw deleteError

        // Puis insérer les nouvelles parties
        const allParties = [
          ...(updates.former_parties?.map((p) => ({
            mutation_id: mutationId,
            owner_id: p.owner_id,
            party_type: 'former' as const,
            share_numerator: p.share_numerator,
            share_denominator: p.share_denominator,
            ownership_type: p.ownership_type || 'full',
          })) || []),
          ...(updates.new_parties?.map((p) => ({
            mutation_id: mutationId,
            owner_id: p.owner_id,
            party_type: 'new' as const,
            share_numerator: p.share_numerator,
            share_denominator: p.share_denominator,
            ownership_type: p.ownership_type || 'full',
          })) || []),
        ]

        if (allParties.length > 0) {
          const { error: insertError } = await supabase
            .from('unit_mutation_parties')
            .insert(allParties)

          if (insertError) throw insertError
        }
      }
    },
    onSuccess: (_, variables) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['unit-mutations', variables.unitId] })
      queryClient.invalidateQueries({ queryKey: ['unit-ownerships', variables.unitId] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}
